<template>
  <div class="vacancy-page-layout">
    
    <DefaultHeader
      :loading="isLoading"
       subtitle="Описание вакансии"
      :title="vacancy?.title || ''"
      :offset="740"
    />
    <div
      class="vacancy-scrollable-content"
      @click.capture="handleGuestActionCapture"
    >
  <div class="vacancy-container">

    <div v-if="showToast" class="toast">
      {{ toastMessage }}
    </div>

    <div v-if="isLoading" class="skeleton-container">
      <div class="skeleton-image"></div>
      <div class="skeleton-title"></div>
      <div class="skeleton-line"></div>
      <div class="skeleton-line short"></div>
    </div>
    
    <template v-else>

    <div class="vacancy-left-block">
      <div class="vacancy-section">
      <div class="vacancy-image-wrapper">
        <div class="vacancy-title-overlay">

          <div class="vacancy-hero-info">

            <h1 class="vacancy-title-overlay-text">
              {{ vacancy?.title }}
            </h1>

            <div class="vacancy-meta-row">

              <div class="vacancy-company-line">
                <span class="company-prefix">
                  в
                </span>

                <span class="vacancy-company">
                  {{ vacancy.company_name }}
                </span>
              </div>


              <span
                v-if="vacancy.company_region_id"
                class="vacancy-city-badge"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M12 21C12 21 18 15.8 18 10.5C18 6.91 15.31 4 12 4C8.69 4 6 6.91 6 10.5C6 15.8 12 21 12 21Z"
                    stroke="currentColor"
                    stroke-width="1.7"
                  />

                  <circle
                    cx="12"
                    cy="10"
                    r="2"
                    stroke="currentColor"
                    stroke-width="1.7"
                  />
                </svg>

                {{ getRegionName(vacancy.company_region_id) }}
              </span>


              <span
                v-if="formattedDate"
                class="vacancy-published-date"
              >
                <span class="published-prefix">
                  размещено
                </span>

                {{ formattedDate }}
              </span>

            </div>

            </div>

        </div>
        <img 
          v-if="vacancy?.image_url" 
          :src="vacancy.image_url"
          alt="Вакансия" 
          class="vacancy-image" 
          :class="{ 'loaded': isImageLoaded }"
          @load="isImageLoaded = true"
        />
        
      </div>

      <div class="vacancy-title-wrapper">
        <button
          type="button"
          class="like-button"
          :class="{
            active: isLiked,
            loading: isLikeUpdating,
            success: isLikeAnimating
          }"
          :disabled="isLikeUpdating"
          :aria-pressed="isLiked"
          :aria-label="
            isLiked
              ? 'Удалить вакансию из избранного'
              : 'Добавить вакансию в избранное'
          "
          @click="toggleLike"
        >
          <img
            :src="isLiked ? likeActive : likeInactive"
            alt=""
            class="like-icon"
          />

          <Transition name="like-loader">
            <span
              v-if="isLikeUpdating"
              class="like-spinner"
            ></span>
          </Transition>

          <span
            v-if="isLikeAnimating"
            class="like-ripple"
          ></span>
        </button>
      </div>

      <button
        class="apply-button"
        :class="{
          applied: vacancy?.type === 0 && hasApplied,
          loading: vacancy?.type === 0 && isApplying,
          'apply-button-email': vacancy?.type === 1 && showEmail
        }"
        :disabled="
          isApplying ||
          (
            isApplyButtonDisabled &&
            !(vacancy?.type === 1 && showEmail)
          )
        "
        @click="
          vacancy?.type === 1 && showEmail
            ? openEmailClient()
            : handleApplyClick()
        "
      >
        <Transition name="apply-content" mode="out-in">
          <span
            v-if="vacancy?.type === 0 && isApplying"
            key="loading"
            class="apply-loading-content"
          >
            <span class="apply-spinner"></span>
            <span>Отправляем отклик</span>
          </span>

          <span
            v-else
            key="default"
            class="apply-default-content"
          >
            <svg
              v-if="vacancy?.type === 0 && hasApplied"
              class="apply-success-icon"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m5.5 10.2 2.8 2.8 6.2-6.2"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            {{ getApplyButtonText }}
          </span>
        </Transition>

        <span
          v-if="vacancy?.type === 0 && isApplying"
          class="apply-button-shine"
        ></span>
      </button>

      <div v-if="vacancy?.type === 1 && showEmail" class="email-hint">
        💬 Работодатель предпочитает связь по email
      </div>

      <div class="about-vacancy">
        <h2 class="about-title">Описание вакансии</h2>
        <div class="about-description" v-html="formattedDescription"></div>
      </div>

      <div class="vacancy-hashtags" v-if="vacancy?.hashtags?.length">
        <span v-for="(tag, index) in vacancy.hashtags" :key="index" class="hashtag">
          #{{ tag.name }}
        </span>
      </div>

      </div>
    </div>

      <div class="company-panel">
        <CompanyHandler
          v-if="vacancy?.company_id"
          :name="vacancy.company_name"
          :specifics="vacancy.company_specifics"
          :logo-url="vacancy.company_logo_url"
          :background-url="vacancy.company_url_of_background"
          :company-id="vacancy.company_id"
          :region="String(vacancy.company_region_id)"
          :about="vacancy.company_about_company"
          @companyClicked="goToCompany"
        />

        <WantToWorkCard
          :loading="isGenerating"
          @click="showAiCoverLetterConfirmation"
        />

<div
  v-if="vacancy"
  class="company-meta-card"
>
  <div class="meta-section">

    <div class="meta-icon-shell">
      <img
        class="meta-icon"
        :src="getSpecificIcon(vacancy.specific._id)"
        alt="Специфика"
      />
    </div>

    <div class="meta-content">
      <p class="meta-title">
        Специфика
      </p>

      <p class="meta-value">
        {{ vacancy.specific.name || "Не указана" }}
      </p>
    </div>

  </div>


      <div class="meta-divider"></div>


      <div class="meta-section">

        <div class="meta-icon-shell salary">
          <span class="meta-icon-dollar">
            $
          </span>
        </div>

        <div class="meta-content">
          <p class="meta-title">
            Зарплата
          </p>

          <p class="meta-value salary-value">
            {{
              vacancy?.salary
                ? (
                    vacancy.salary.includes("обсуждается")
                      ? vacancy.salary
                      : vacancy.salary + " ₽"
                  )
                : "Не указана"
            }}
          </p>
        </div>

      </div>
    </div>

      </div>
      <div class="office-block">
        <h2 class="office-title">Офис</h2>
        <p class="office-address">{{ vacancy?.address || "Адрес не указан" }}</p>
          <GoogleMap class="map-wrapper"
            v-if="vacancy?.latitude && vacancy?.longitude" 
            :lat="vacancy.latitude" 
            :lng="vacancy.longitude"
            :logo-url="vacancy.company_logo_url"
          />
      </div>

    </template> 
  </div>

  <div
      v-if="!isCheckingAdmin && isAdmin"
      class="admin-actions-block"
    >

      <button
        type="button"
        class="edit-vacancy-button"
        @click="openAdminEditVacancy"
      >

        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M14.5 5.5L18.5 9.5"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />

          <path
            d="M6 18L7 14L16.5 4.5C17.33 3.67 18.67 3.67 19.5 4.5C20.33 5.33 20.33 6.67 19.5 7.5L10 17L6 18Z"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        Редактировать вакансию

      </button>


      <button
        type="button"
        class="delete-button"
        @click="showDeleteModal = true"
      >

        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6 7H18"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />

          <path
            d="M9 7V5H15V7"
            stroke="currentColor"
            stroke-width="1.8"
          />

          <path
            d="M8 7L9 19H15L16 7"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linejoin="round"
          />
        </svg>

        Удалить вакансию

      </button>

    </div>

  </div>
    <Footer v-if="!isLoading" class="feed-footer" />
  </div>

  <DeleteVacancyModal
    :visible="showDeleteModal"
    @close="showDeleteModal = false"
    @confirm="confirmDelete"
  />

  <AiCoverLetterGeneratingOverlay
    v-if="isGenerating"
    :vacancy-title="vacancy?.title || ''"
    :company-name="vacancy?.company_name || ''"
  />

  <AiCoverLetterConfirmModal
    v-if="showAiCoverLetterConfirm"
    :vacancy-title="vacancy?.title || ''"
    :company-name="vacancy?.company_name || ''"
    @close="closeAiCoverLetterConfirmation"
    @confirm="confirmAiCoverLetterGeneration"
  />

  <AiCoverLetterPreview
    v-if="showPreview"
    :letter="generatedLetter"
    @close="showPreview = false"
    @send="sendToEmployer"
  />

  <NotLoggedInOverlay
    :visible="showNotLoggedInOverlay"
    @login="goToLogin"
    @register="goToRegistration"
  />

</template>
<script setup>

import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount
} from "vue";

import {
  useRoute,
  useRouter
} from "vue-router";

import {
  getAuth,
  onAuthStateChanged
} from "firebase/auth";


import {
  SettingsService
} from "../api/SettingsService";

import {
  AnalyticsService,
  AnalyticsEvent
} from "../utils/AnalyticsService";

import AiCoverLetterConfirmModal
  from "../components/AiCoverLetterConfirmModal.vue";

import VacancyService
  from "../api/VacancyService.js";

import AdminService
  from "../api/AdminService.js";

import PremiumService
  from "../api/PremiumService.js";

import SpecificsService
  from "../utils/SpecificsService.js";

import AiCoverLetterGeneratingOverlay
  from "../components/AiCoverLetterGeneratingOverlay.vue";


import DefaultHeader
  from "../components/VacancyHeader.vue";

import CompanyHandler
  from "../components/CompanyHandler.vue";

import Footer
  from "../components/MiniFooter.vue";

import GoogleMap
  from "../components/GoogleMap.vue";

import DeleteVacancyModal
  from "../components/DeleteVacancyModal.vue";

import WantToWorkCard
  from "../components/WantToWorkCard.vue";

import AiCoverLetterPreview
  from "../components/AiCoverLetterPreview.vue";

import NotLoggedInOverlay
  from "../components/NotLoggedInOverlay.vue";


import likeActive
  from "../../public/like_active.png";

import likeInactive
  from "../../public/like_not_active.png";

import UserService
  from "../api/UserService.js";


/* ============================================================
   ROUTER
============================================================ */

const route =
  useRoute();

const router =
  useRouter();


/* ============================================================
   VACANCY
============================================================ */

const vacancy =
  ref(null);

const isLoading =
  ref(true);

const isImageLoaded =
  ref(false);


/* ============================================================
   AUTH
============================================================ */

const isAuthResolved =
  ref(false);

const isLoggedIn =
  ref(false);

const showNotLoggedInOverlay =
  ref(false);

let guestOverlayTimer =
  null;

let unsubscribeAuth =
  null;

const showAiCoverLetterConfirm =
  ref(false);


/* ============================================================
   ADMIN
============================================================ */

const isAdmin =
  ref(false);

const isCheckingAdmin =
  ref(true);

const showDeleteModal =
  ref(false);


/* ============================================================
   LIKE
============================================================ */

const isLiked =
  ref(false);

const isLikeUpdating =
  ref(false);

const isLikeAnimating =
  ref(false);


/* ============================================================
   APPLY
============================================================ */

const hasApplied =
  ref(false);

const isApplying =
  ref(false);

const showEmail =
  ref(false);


/* ============================================================
   AI COVER LETTER
============================================================ */

const showPreview =
  ref(false);

const generatedLetter =
  ref(null);

const isGenerating =
  ref(false);


/* ============================================================
   PAYWALL
============================================================ */

const showPaywall =
  ref(false);


/* ============================================================
   SNACKBAR / TOAST
============================================================ */

const snackbarMessage =
  ref("");

const snackbarType =
  ref("success");

const showSnackbar =
  ref(false);

const showToast =
  ref(false);

const toastMessage =
  ref("");


/* ============================================================
   TOAST
============================================================ */

const triggerToast = (
  message
) => {

  toastMessage.value =
    message;

  showToast.value =
    true;


  window.setTimeout(
    () => {

      showToast.value =
        false;

    },
    2000
  );

};


/* ============================================================
   SPECIFICS
============================================================ */

const getSpecificIcon = (
  specificId
) => {

  console.log(
    "🔍 Специфика ID из вакансии:",
    specificId
  );


  const match =
    SpecificsService
      .getSpecifics()
      .find(
        item =>
          item.id === specificId
      );


  console.log(
    "🎯 Найденная иконка:",
    match?.icon
  );


  return (
    match?.icon ||
    "/specifics/default.png"
  );

};


/* ============================================================
   EDIT VACANCY — ADMIN
   Передаём полные данные вакансии в редактор
============================================================ */

const openAdminEditVacancy = async () => {
  if (
    isCheckingAdmin.value ||
    !isAdmin.value ||
    !vacancy.value?._id
  ) {
    return;
  }

  try {
    const source = vacancy.value;

    // Сохраняем все исходные поля вакансии
    // и приводим некоторые названия к формату редактора.
    const vacancyToEdit = {
      ...source,

      id: source._id,

      image_url:
        source.image_url ??
        source.imageURL ??
        "",

      isRemote: Boolean(
        source.isRemote ??
        source.is_remote ??
        false
      ),

      street:
        source.street ??
        source.address ??
        "",

      specific: {
        ...(source.specific || {}),

        id:
          source.specific?.id ??
          source.specific?._id ??
          "",

        name:
          source.specific?.name ??
          "",

        eng_name:
          source.specific?.eng_name ??
          ""
      },

      hashtags: Array.isArray(source.hashtags)
        ? source.hashtags
        : []
    };

    // Передаём весь объект редактору
    localStorage.setItem(
      "admin_vacancy_to_edit",
      JSON.stringify(vacancyToEdit)
    );

    // Удаляем устаревшие данные адреса предыдущей вакансии
    localStorage.removeItem("vacancy_address");
    localStorage.removeItem("vacancy_is_manual");

    console.log(
      "Вакансия передана на редактирование:",
      vacancyToEdit
    );

    await router.push(
      `/admin-edit-vacancy/${encodeURIComponent(source._id)}`
    );

  } catch (error) {
    console.error(
      "Ошибка открытия редактора вакансии:",
      error
    );

    triggerToast(
      "Не удалось открыть редактор вакансии"
    );
  }
};


/* ============================================================
   AUTH HELPERS
============================================================ */

function clearGuestOverlayTimer() {

  if (!guestOverlayTimer) {
    return;
  }


  window.clearTimeout(
    guestOverlayTimer
  );


  guestOverlayTimer =
    null;

}


function showGuestAuthOverlay() {

  clearGuestOverlayTimer();


  showNotLoggedInOverlay.value =
    true;

}


function scheduleGuestOverlay() {

  clearGuestOverlayTimer();


  guestOverlayTimer =
    window.setTimeout(
      () => {

        if (
          isAuthResolved.value &&
          !isLoggedIn.value
        ) {

          showNotLoggedInOverlay.value =
            true;

        }

      },
      3000
    );

}


/*
 * Возвращает true, если действие
 * разрешено выполнять.
 *
 * Для гостя сразу показывает overlay.
 */
function requireAuth() {

  if (
    !isAuthResolved.value
  ) {

    return false;

  }


  if (
    isLoggedIn.value
  ) {

    return true;

  }


  showGuestAuthOverlay();


  return false;

}


/* ============================================================
   ADMIN STATE
============================================================ */

async function resolveAdminState(
  user
) {

  isCheckingAdmin.value =
    true;


  try {

    /*
     * Гость по определению
     * не может быть администратором.
     */

    if (!user) {

      isAdmin.value =
        false;

      return;

    }


    const localAdmin =
      localStorage.getItem(
        "is_admin"
      ) === "true";


    if (!localAdmin) {

      isAdmin.value =
        false;

      console.log(
        "⚠️ Локальный is_admin=false"
      );

      return;

    }


    console.log(
      "🔍 Проверяем роль администратора через API..."
    );


    const settings =
      await SettingsService
        .fetchSettings();


    isAdmin.value =
      Boolean(
        settings?.isAdmin
      );


    console.log(
      isAdmin.value
        ? "👑 Подтверждено: пользователь — администратор"
        : "🚫 Пользователь не подтверждён как админ"
    );

  } catch (error) {

    console.error(
      "❌ Ошибка проверки администратора:",
      error
    );


    isAdmin.value =
      false;

  } finally {

    isCheckingAdmin.value =
      false;

  }

}


/* ============================================================
   AUTH STATE
============================================================ */

function setupAuthState() {

  const auth =
    getAuth();


  unsubscribeAuth =
    onAuthStateChanged(
      auth,
      async user => {

        isLoggedIn.value =
          Boolean(user);

        isAuthResolved.value =
          true;


        console.log(
          user
            ? "👤 Пользователь авторизован"
            : "🌍 Страница открыта гостем"
        );


        await resolveAdminState(
          user
        );


        /*
         * Авторизован:
         * никакой guest overlay не нужен.
         */

        if (user) {

          clearGuestOverlayTimer();


          showNotLoggedInOverlay.value =
            false;


          return;

        }


        /*
         * Гость:
         * даём спокойно посмотреть страницу,
         * спустя 3 секунды показываем overlay.
         */

        scheduleGuestOverlay();

      }
    );

}


/* ============================================================
   GUEST ACTION CAPTURE
============================================================ */

function handleGuestActionCapture(
  event
) {

  if (
    !isAuthResolved.value
  ) {

    return;

  }


  if (
    isLoggedIn.value
  ) {

    return;

  }


  const target =
    event.target;


  if (
    !(target instanceof Element)
  ) {

    return;

  }


  /*
   * Перехватываем именно интерактивные
   * элементы внутри страницы вакансии.
   */

  const actionElement =
    target.closest(
      `
        button,
        a,
        [role="button"],
        [data-auth-action]
      `
    );


  if (
    !actionElement
  ) {

    return;

  }


  event.preventDefault();

  event.stopPropagation();

  event.stopImmediatePropagation?.();


  showGuestAuthOverlay();

}


/* ============================================================
   NOT LOGGED IN OVERLAY
============================================================ */

function closeNotLoggedInOverlay() {

  showNotLoggedInOverlay.value =
    false;

}


async function goToLogin() {

  showNotLoggedInOverlay.value =
    false;


  const redirect =
    route.fullPath;


  await router.push({

    path:
      "/login",

    query: {
      redirect
    }

  });

}


async function goToRegistration() {

  showNotLoggedInOverlay.value =
    false;


  const redirect =
    route.fullPath;


  await router.push({

    path:
      "/registration",

    query: {
      redirect
    }

  });

}


/* ============================================================
   FORBIDDEN
============================================================ */

function isForbidden(
  error
) {

  return (
    error?.response?.status === 403 ||
    error?.status === 403
  );

}


/* ============================================================
   COMPANY ID FOR AI
============================================================ */

function getCompanyIdForCoverLetter() {

  return (
    vacancy.value?.company_id ||
    vacancy.value?.companyId ||
    vacancy.value?.company_id_vacancy ||
    route.params.companyId ||
    null
  );

}


/* ============================================================
   AI COVER LETTER
============================================================ */

async function openAiCoverLetter() {

  /*
   * Дополнительная защита.
   *
   * Capture должен поймать UI-клик,
   * но бизнес-функция тоже сама
   * защищена от гостевого вызова.
   */

  if (!requireAuth()) {
    return;
  }


  if (
    isGenerating.value
  ) {

    return;

  }


  showPreview.value =
    false;

  generatedLetter.value =
    null;

  isGenerating.value =
    true;


  try {

    const companyId =
      getCompanyIdForCoverLetter();


    if (!companyId) {

      throw new Error(
        "Company ID отсутствует"
      );

    }


    const letter =
      await PremiumService
        .generateCoverLetter(
          companyId
        );


    if (!letter) {

      throw new Error(
        "Сервис не вернул сопроводительное письмо"
      );

    }


    generatedLetter.value =
      letter;

    showPreview.value =
      true;

  } catch (error) {

    console.error(
      "❌ Ошибка AI генерации:",
      error
    );


    if (
      isForbidden(
        error
      )
    ) {

      showPaywall.value =
        true;

      return;

    }


    snackbarMessage.value =
      error?.response?.data?.error ||
      error?.message ||
      "Не удалось сгенерировать сопроводительное письмо";


    snackbarType.value =
      "error";

    showSnackbar.value =
      true;

  } finally {

    isGenerating.value =
      false;

  }

}


/* ============================================================
   SEND AI LETTER
============================================================ */

async function sendToEmployer() {

  if (!requireAuth()) {
    return;
  }


  const companyId =
    getCompanyIdForCoverLetter();


  if (
    !companyId ||
    !generatedLetter.value
  ) {

    return;

  }


  try {

    isGenerating.value =
      true;


    await PremiumService
      .sendCoverLetter(
        companyId,
        generatedLetter.value
      );


    snackbarMessage.value =
      "Письмо отправлено работодателю!";

    snackbarType.value =
      "success";

    showSnackbar.value =
      true;


    showPreview.value =
      false;

  } catch (error) {

    console.error(
      "❌ Ошибка отправки письма:",
      error
    );


    if (
      isForbidden(
        error
      )
    ) {

      showPaywall.value =
        true;

      return;

    }


    snackbarMessage.value =
      "Не удалось отправить письмо";

    snackbarType.value =
      "error";

    showSnackbar.value =
      true;

  } finally {

    AnalyticsService.log(
      AnalyticsEvent
        .SEND_USER_COVER_AI_LETTER
    );


    isGenerating.value =
      false;

  }

}


/* ============================================================
   APPLY CLICK
============================================================ */

const handleApplyClick =
  async () => {

    if (!requireAuth()) {
      return;
    }


    if (
      !vacancy.value ||
      isApplying.value
    ) {

      return;

    }

    updateAssumeSpecificsSilently();


    switch (
      vacancy.value.type
    ) {

      /*
       * Обычный внутренний отклик.
       */

      case 0:

        await applyForVacancy();

        break;


      case 1:

        if (
          vacancy.value
            .is_email_handling
        ) {

          showEmail.value =
            true;

        } else {

          triggerToast(
            "Email не указан"
          );

        }

        break;


      /*
       * Партнёрская ссылка.
       */

      case 2:

        if (
          vacancy.value
            .partner_url
        ) {

          let url =
            vacancy.value
              .partner_url
              .trim();


          if (
            !/^https?:\/\//i.test(
              url
            )
          ) {

            url =
              `https://${url}`;

          }


          console.log(
            "🔗 Открываем партнёрскую ссылку:",
            url
          );


          window.open(
            url,
            "_blank",
            "noopener,noreferrer"
          );

        }

        break;


      default:
        break;

    }

  };


/* ============================================================
   EMAIL
============================================================ */

const openEmailClient =
  () => {

    if (!requireAuth()) {
      return;
    }


    const email =
      vacancy.value
        ?.is_email_handling;


    if (!email) {
      return;
    }


    window.location.href =
      `mailto:${email}`;

  };


/* ============================================================
   APPLY BUTTON TEXT
============================================================ */

const getApplyButtonText =
  computed(
    () => {

      if (
        !vacancy.value
      ) {

        return "";

      }


      switch (
        vacancy.value.type
      ) {

        case 0:

          return (
            hasApplied.value
              ? "Вы откликнулись"
              : "Откликнуться"
          );


        case 1:

          return (
            showEmail.value
              ? (
                  vacancy.value
                    .is_email_handling ||
                  "Email не указан"
                )
              : "Откликнуться"
          );


        case 2:

          return "Откликнуться";


        default:

          return "Откликнуться";

      }

    }
  );


/* ============================================================
   APPLY DISABLED
============================================================ */

const isApplyButtonDisabled =
  computed(
    () => {

      if (
        !vacancy.value
      ) {

        return false;

      }


      return (
        (
          vacancy.value.type === 0 &&
          hasApplied.value
        ) ||
        (
          vacancy.value.type === 1 &&
          showEmail.value
        )
      );

    }
  );


/* ============================================================
   APPLY
============================================================ */

const applyForVacancy =
  async () => {

    if (!requireAuth()) {
      return;
    }


    if (
      !vacancy.value?._id ||
      isApplying.value ||
      hasApplied.value
    ) {

      return;

    }


    isApplying.value =
      true;


    try {

      console.log(
        "📨 Отправляем отклик:",
        vacancy.value._id
      );


      const success =
        await VacancyService
          .createResponse(
            vacancy.value._id
          );


      if (!success) {

        triggerToast(
          "Не удалось отправить отклик"
        );

        return;

      }


      hasApplied.value =
        true;


      triggerToast(
        "Вы успешно откликнулись"
      );


      AnalyticsService.log(
        AnalyticsEvent
          .APPLY_VACANCY
      );


      console.log(
        "✅ Отклик успешно отправлен"
      );

    } catch (error) {

      console.error(
        "❌ Ошибка при отправке отклика:",
        error
      );


      triggerToast(
        "Ошибка при отправке отклика"
      );

    } finally {

      isApplying.value =
        false;

    }

  };


/* ============================================================
   LIKE
============================================================ */

const toggleLike =
  async () => {

    if (!requireAuth()) {
      return;
    }


    if (
      !vacancy.value?._id ||
      isLikeUpdating.value
    ) {

      return;

    }


    isLikeUpdating.value =
      true;

    isLikeAnimating.value =
      false;


    try {

      console.log(
        "❤️ Изменяем состояние избранного:",
        vacancy.value._id
      );


      const result =
        await VacancyService
          .toggleFavorite(
            vacancy.value._id
          );


      if (
        !result?.success
      ) {

        triggerToast(
          "Ошибка при изменении избранного"
        );

        return;

      }


      isLiked.value =
        Boolean(
          result.isFavorite
        );


      isLikeAnimating.value =
        true;


      AnalyticsService.log(
        AnalyticsEvent
          .SAVE_VACANCY_TO_FAVORITE
      );


      triggerToast(
        result.isFavorite
          ? "Вакансия добавлена в избранное"
          : "Вакансия удалена из избранного"
      );


      console.log(
        result.isFavorite
          ? "✅ Добавлено в избранное"
          : "✅ Удалено из избранного"
      );


      window.setTimeout(
        () => {

          isLikeAnimating.value =
            false;

        },
        600
      );

    } catch (error) {

      console.error(
        "❌ Ошибка при изменении избранного:",
        error
      );


      triggerToast(
        "Не удалось изменить избранное"
      );

    } finally {

      isLikeUpdating.value =
        false;

    }

  };


/* ============================================================
   COMPANY
============================================================ */

const goToCompany =
  async (
    companyId
  ) => {

    /*
     * Ты хотел, чтобы любое действие
     * гостя приводило к auth overlay.
     *
     * Поэтому переход в компанию
     * тоже защищаем.
     */

    if (!requireAuth()) {
      return;
    }


    if (!companyId) {
      return;
    }


    console.log(
      "📌 Переход на компанию:",
      companyId
    );


    await router.push(
      `/company/${companyId}`
    );

  };


/* ============================================================
   REGION
============================================================ */

const getRegionName =
  id => {

    if (
      Number(id) === 1
    ) {

      return "Москва";

    }


    if (
      Number(id) === 2
    ) {

      return "Санкт-Петербург";

    }


    return "Регион не указан";

  };


/* ============================================================
   DESCRIPTION
============================================================ */

const formattedDescription =
  computed(
    () => {

      const text =
        vacancy.value?.info ??
        "";


      const esc =
        value =>
          value.replace(
            /[&<>"']/g,
            char => ({
              "&": "&amp;",
              "<": "&lt;",
              ">": "&gt;",
              "\"": "&quot;",
              "'": "&#39;"
            })[char]
          );


      const escapedText =
        esc(
          text
        );


      const blocks =
        escapedText.split(
          /\n\s*\n/
        );


      return blocks
        .map(
          block => {

            const lines =
              block
                .split(
                  /\n/
                )
                .map(
                  line =>
                    line.trim()
                )
                .filter(
                  Boolean
                );


            if (
              !lines.length
            ) {

              return "";

            }


            const firstLine =
              lines[0];


            const restLines =
              lines.slice(
                1
              );

            const restIsList =
              restLines.length > 0 &&
              restLines.every(
                line =>
                  /^(\u2014|-)\s+/
                    .test(
                      line
                    )
              );


            if (
              restIsList
            ) {

              const items =
                restLines
                  .map(
                    line =>
                      line.replace(
                        /^(\u2014|-)\s+/,
                        ""
                      )
                  )
                  .map(
                    item =>
                      `<li>${item}</li>`
                  )
                  .join(
                    ""
                  );


              return `
                <section class="description-block">
                  <h3 class="description-heading">
                    ${firstLine}
                  </h3>

                  <ul>
                    ${items}
                  </ul>
                </section>
              `;

            }


            /*
             * Блок целиком состоит
             * из элементов списка.
             */

            const allLinesAreList =
              lines.every(
                line =>
                  /^(\u2014|-)\s+/
                    .test(
                      line
                    )
              );


            if (
              allLinesAreList
            ) {

              const items =
                lines
                  .map(
                    line =>
                      line.replace(
                        /^(\u2014|-)\s+/,
                        ""
                      )
                  )
                  .map(
                    item =>
                      `<li>${item}</li>`
                  )
                  .join(
                    ""
                  );


              return `
                <ul>
                  ${items}
                </ul>
              `;

            }


            /*
             * Обычный текст.
             */

            return `
              <p>
                ${lines.join("<br>")}
              </p>
            `;

          }
        )
        .join(
          ""
        );

    }
  );


/* ============================================================
   DATE
============================================================ */

const formattedDate =
  computed(
    () => {

      const date =
        vacancy.value
          ?.date_of_create;


      if (!date) {
        return null;
      }


      const rawValue =
        Number(
          date
        );


      if (
        !Number.isFinite(
          rawValue
        )
      ) {

        return null;

      }


      /*
       * Поддерживаем timestamp
       * как в секундах,
       * так и в миллисекундах.
       */

      const timestamp =
        rawValue < 10_000_000_000
          ? rawValue * 1000
          : rawValue;


      const vacancyDate =
        new Date(
          timestamp
        );


      const now =
        new Date();


      const normalize =
        value =>
          new Date(
            value.getFullYear(),
            value.getMonth(),
            value.getDate()
          );


      const diffDays =
        Math.floor(
          (
            normalize(now) -
            normalize(vacancyDate)
          ) /
          (
            1000 *
            60 *
            60 *
            24
          )
        );


      if (
        diffDays === 0
      ) {

        return "Сегодня";

      }


      if (
        diffDays === 1
      ) {

        return "Вчера";

      }


      if (
        diffDays === 2
      ) {

        return "Позавчера";

      }


      return vacancyDate
        .toLocaleDateString(
          "ru-RU",
          {
            day:
              "numeric",

            month:
              "long",

            year:
              "numeric"
          }
        );

    }
  );


/* ============================================================
   DELETE VACANCY — ADMIN
============================================================ */

const confirmDelete =
  async () => {

    if (
      !vacancy.value?._id
    ) {

      return;

    }


    try {

      const result =
        await AdminService
          .deleteAdminVacancy(
            vacancy.value._id
          );


      if (
        result?.success
      ) {

        showDeleteModal.value =
          false;


        await router.push(
          "/feed"
        );


        return;

      }


      triggerToast(
        "Не удалось удалить вакансию"
      );

    } catch (error) {

      console.error(
        "❌ Ошибка при удалении:",
        error
      );


      triggerToast(
        "Ошибка удаления"
      );

    } finally {

      showDeleteModal.value =
        false;

    }

  };


/* ============================================================
   LOAD VACANCY
============================================================ */
async function loadVacancy() {
  const vacancyId = route.params.vacancyId;

  if (!vacancyId) {
    console.warn("⚠️ vacancyId отсутствует");
    await router.replace("/404");
    return;
  }

  isLoading.value = true;

  try {
    console.log("📦 Загружаем вакансию:", vacancyId);

    const result = await VacancyService.fetchVacancyById(vacancyId);

    if (!result) {
      console.warn("⚠️ Вакансия не найдена:", vacancyId);
      await router.replace("/404");
      return;
    }

    vacancy.value = result;

    isLiked.value = Boolean(vacancy.value.is_liked);
    hasApplied.value = Boolean(vacancy.value.is_responded);

    console.log("✅ Вакансия загружена:", vacancy.value);

    AnalyticsService.log(
      AnalyticsEvent.VIEW_VACANCY
    );
  } catch (error) {
    const status =
      error?.response?.status ??
      error?.status ??
      null;

    console.error(
      "❌ Ошибка загрузки вакансии:",
      error?.response?.data ||
      error?.message ||
      error
    );

    if (status === 404) {
      console.warn("⚠️ Вакансия удалена или не существует:", vacancyId);
      await router.replace("/404");
      return;
    }

    throw error;
  } finally {
    isLoading.value = false;
  }
}


/* ============================================================
   LIFECYCLE
============================================================ */

onMounted(
  async () => {
    setupAuthState();
    await loadVacancy();

  }
);


/* ============================================================
   CLEANUP
============================================================ */

onBeforeUnmount(
  () => {

    clearGuestOverlayTimer();


    if (
      unsubscribeAuth
    ) {

      unsubscribeAuth();


      unsubscribeAuth =
        null;

    }

  }
);

function showAiCoverLetterConfirmation() {

if (!requireAuth()) {
  return;
}

if (isGenerating.value) {
  return;
}

showAiCoverLetterConfirm.value =
  true;
}


function closeAiCoverLetterConfirmation() {

showAiCoverLetterConfirm.value =
  false;
}


async function confirmAiCoverLetterGeneration() {

showAiCoverLetterConfirm.value =
  false;

await openAiCoverLetter();
}

/* ============================================================
   ASSUME SPECIFICS — BACKGROUND SYNC
============================================================ */

function updateAssumeSpecificsSilently() {

const specificId =
  vacancy.value?.specific?._id ??
  vacancy.value?.specific?.id ??
  null;

if (!specificId) {

  console.warn(
    "⚠️ [assumeSpecifics] У вакансии отсутствует specific id"
  );

  return;
}

console.log(
  "🎯 [assumeSpecifics] Фоновое обновление:",
  specificId
);

UserService
  .updateAssumeSpecifics(
    String(specificId)
  )
  .then(() => {

    console.log(
      "✅ [assumeSpecifics] Специфика обновлена:",
      specificId
    );

  })
  .catch(error => {

    /*
     * Намеренно ничего не показываем пользователю.
     *
     * Основное действие:
     * отклик / email / переход на партнёра
     * не должно зависеть от этого запроса.
     */

    console.warn(
      "⚠️ [assumeSpecifics] Фоновое обновление не удалось:",
      error?.response?.data ||
      error?.message ||
      error
    );

  });
}

</script>

<style scoped>

:root {
  --vacancy-bg-dark: #161616;
  --vacancy-bg-light: #F8F9FA;
  --text-color-dark: white;
  --text-color-light: black;
  --separator-dark: #2a2a2a;
  --separator-light: #E7E7E7;
}

html,
body {
  overflow-x: hidden;
}

.vacancy-container {
  max-width: 100vw;
  overflow-x: hidden;
  position: relative;
}

[data-theme="light"] .vacancy-container {
  background-color: var(--vacancy-bg-light);
  color: var(--text-color-light);
}


[data-theme="light"] .separator {
  background-color: var(--separator-light);
}

.vacancy-image-wrapper {
  position: relative;
  width: 100%;
  height: 300px;
  justify-content: flex-start;
  background-color: var(--vacancy-bg-dark);
  overflow: hidden;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  background: #e0e0e0;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  border-radius: 10px;
}

.vacancy-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  border-radius: 0;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.vacancy-image.loaded {
  opacity: 1;
}

.vacancy-image-wrapper::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 60%, rgba(0, 0, 0, 0.7) 100%);
  pointer-events: none;
}

[data-theme="light"] .vacancy-image-wrapper {
  background-color: var(--vacancy-bg-light);
}

.vacancy-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: calc(50% + 330px);
  width: 960px;
}

.vacancy-title {
  font-size: 20px;
  font-family: "Eastman-Bold", sans-serif;
  color: var(--vacancy-title-dark);
}

.vacancy-title-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 35%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 16px 32px;
  z-index: 2;
}

.vacancy-title-overlay-text {
  font-size: 22px;
  font-family: "Eastman-Bold", sans-serif;
  color: white;
  margin: 0;
  letter-spacing: -0.5px;
}

[data-theme="light"] .vacancy-title {
  color: var(--vacancy-title-light);
}

.like-icon {
  width: 22px;
  height: 18px;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;
  position: relative;
  z-index: 5;
}

.like-icon:hover {
  transform: scale(1.1);
  opacity: 0.8;
}

.about-vacancy {
  width: 90%;
  margin-left: 30px;
  margin-top: 40px;
}

.about-title {
  font-size: 12px;
  font-family: "Eastman-Medium", sans-serif;
  color: var(--vacancy-title-dark);
  margin-bottom: 10px;
}


/* ============================================================
   NORMAL PARAGRAPH
============================================================ */

.about-description :deep(p) {
  margin:
    0
    0
    12px;

  color: #666b72;
}


.about-description :deep(p:last-child) {
  margin-bottom: 0;
}


/* ============================================================
   LIST
============================================================ */

.about-description :deep(ul) {
  display: flex;
  flex-direction: column;

  gap: 3px;

  margin: 0;

  padding: 0;

  list-style: none;
}


.about-description :deep(li) {
  position: relative;

  margin: 0;

  padding-left: 14px;

  color: #666b72;

  line-height: 1.48;
}


.about-description :deep(li::before) {
  content: "";

  position: absolute;

  left: 1px;
  top: 0.72em;

  width: 3px;
  height: 3px;

  background: #343434;

  border-radius: 50%;

  transform:
    translateY(-50%);
}


/* ============================================================
   LINKS
============================================================ */

.about-description :deep(a) {
  color: #2879e8;

  text-decoration: none;

  border-bottom:
    1px solid
    rgba(
      40,
      121,
      232,
      0.2
    );
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.about-description {
  color: #bfc3c9;
}


html[data-theme="dark"]
.about-description :deep(.description-heading) {
  color: #d6d8dc;
}


html[data-theme="dark"]
.about-description :deep(p),
html[data-theme="dark"]
.about-description :deep(li) {
  color: #bfc3c9;
}


html[data-theme="dark"]
.about-description :deep(li::before) {
  background: #b7bbc1;
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 430px) {

  .about-description {
    max-width: 100%;

    font-size: 13px;

    line-height: 1.5;
  }


  .about-description :deep(.description-heading) {
    margin-bottom: 5px;

    font-size: 12px;
  }


  .about-description :deep(.description-block) {
    margin-bottom: 14px;
  }


  .about-description :deep(ul) {
    gap: 3px;
  }


  .about-description :deep(li) {
    padding-left: 13px;
  }

}

.office-block {
  margin-top: 40px;
  width: 740px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  background-color: white;
  border-radius: 16px;
  margin-left: calc(50% - 630px);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

[data-theme="dark"] .office-block {
  background-color: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.08);
}


.office-title {
  font-size: 14px;
  font-family: "Eastman-Medium", sans-serif;
  color: var(--vacancy-title-dark);
  margin-bottom: -40px;
  margin-top: 40px;
  margin-left: 30px;
}

.office-address {
  font-size: 13px;
  font-family: "Eastman-Regular", sans-serif;
  color: #626262;
  line-height: 1.4;
  margin-bottom: 0px;
  margin-left: 30px;
  margin-right: 30px;
}

[data-theme="dark"] .office-address {
  color: #ffffff;
}

.office-map {
  width: 740px;
  height: 300px;
  border: none;
  margin-bottom: 40px;
}

.company-panel {
  position: absolute;
  top: 0px;
  left: calc(50% + 150px);
  width: 300px;
  z-index: 2;
}

.company-panel .company-handler {
  background-color: white !important;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

[data-theme="dark"] .company-panel .company-handler {
  background-color: #1b1b1b !important;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.company-title {
  font-size: 16px;
  font-family: "Eastman-Medium", sans-serif;
  color: var(--vacancy-title-dark);
  margin-bottom: 8px;
}

.company-wrapper {
  width: 30%;
  margin-left: calc(50% - 630px);
  margin-top: 8px;
  margin-bottom: 40px;
}

.skeleton-container {
  padding-left: calc(50% - 630px);
  margin-top: 20px;
}

.skeleton-image {
  width: 960px;
  height: 300px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite linear;
  border-radius: 10px;
}

.skeleton-title {
  width: 300px;
  height: 24px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite linear;
  margin-top: 20px;
  border-radius: 5px;
}

.skeleton-line {
  width: 600px;
  height: 16px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite linear;
  margin-top: 10px;
  border-radius: 5px;
}

.skeleton-line.short {
  width: 450px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

.toast {
  position: fixed;
  bottom: 5%;
  left: 50%;
  transform: translateX(-50%);
  background: #2173E5;
  color: white;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-family: "Eastman-Medium", sans-serif;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  opacity: 0;
  animation: fadeInOut 2s ease-in-out;
  z-index: 9999; 
  pointer-events: none;
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translateX(-50%) translateY(10px); }
  20% { opacity: 1; transform: translateX(-50%) translateY(0px); }
  80% { opacity: 1; transform: translateX(-50%) translateY(0px); }
  100% { opacity: 0; transform: translateX(-50%) translateY(10px); }
}

.vacancy-hashtags {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  margin-bottom: 32px;
  margin-left: 30px;
}

.hashtag {
  font-size: 13px;
  font-family: "Eastman-Regular", sans-serif;
  color: white;
  background: #373737;
  padding: 6px 16px;
  border-radius: 10px;
  white-space: nowrap;
  line-height: 1;
  transition: background 0.2s ease;
}

.hashtag:hover {
  background: #dddddd;
  cursor: pointer;
}

[data-theme="light"] .hashtag {
  color: rgb(107, 107, 107);
  background: #edecec;
}

.vacancy-page-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background-color: #EDEDED;
}

[data-theme="dark"] .vacancy-page-layout {
  background-color: #121212;
}

.vacancy-scrollable-content {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 30px;
  padding-top: 30px;
}

.feed-footer {
  flex-shrink: 0;
  width: 100%;
}

.vacancy-left-block {
  width: 740px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  background-color: white;
  border-radius: 16px;
  margin-left: calc(50% - 630px);
}

[data-theme="dark"] .vacancy-left-block {
  background-color: #1e1e1e;
}

.section-title {
  font-size: 18px;
  font-family: "Eastman-Bold", sans-serif;
  margin-bottom: 10px;
  color: var(--vacancy-title-dark);
}

.section-text {
  font-size: 14px;
  font-family: "Eastman-Regular", sans-serif;
  color: var(--vacancy-title-dark);
  line-height: 1.5;
}

.vacancy-section {
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.vacancy-subtitle-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.vacancy-company {
  font-size: 16px;
  font-family: "Eastman-Regular", sans-serif;
  color: rgba(255, 255, 255, 0.85);
}

.vacancy-city-badge {
  font-size: 12px;
  font-family: "Eastman-Medium", sans-serif;
  color: white;
  padding: 4px 15px;
  border-radius: 20px;
  background: linear-gradient(90deg, #2173E5, #8247E2);
  white-space: nowrap;
}

.company-meta-card {
  width: 300px;
  background-color: white;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  overflow: hidden;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
}

[data-theme="dark"] .company-meta-card {
  background-color: #1b1b1b;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.meta-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  text-align: center;
}

.meta-divider {
  height: 1px;
  width: 100%;
  background-color: #f0f0f0;
}

[data-theme="dark"] .meta-divider {
  background-color: #2a2a2a;
}

.meta-icon {
  width: 28px;
  height: 28px;
  margin-bottom: 14px;
}

.meta-title {
  font-size: 12px;
  font-family: "Eastman-Medium", sans-serif;
  margin-bottom: -10px;
  color: #444;
}

[data-theme="dark"] .meta-title {
  color: #bcbcbc;
}

.meta-value {
  font-size: 12px;
  font-family: "Eastman-Light", sans-serif;
  color: rgb(69, 69, 69);
  margin-bottom: 0px;
  line-height: 1.0;
}

[data-theme="dark"] .meta-value {
  color: white;
}

.meta-icon-dollar {
  font-size: 28px;
  font-weight: 800;
  margin-bottom: 10px;
  color: #1e1e1e;
}

[data-theme="dark"] .meta-icon-dollar {
  color: white;
}

[data-theme="dark"] .skeleton-image,
[data-theme="dark"] .skeleton-title,
[data-theme="dark"] .skeleton-line,
[data-theme="dark"] .skeleton-line.short {
  background: linear-gradient(90deg, #333 25%, #444 50%, #333 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite linear;
}

.map-wrapper {
  margin-bottom: 40px;
}

[data-theme="dark"] .meta-icon {
  filter: invert(1) brightness(2);
}

.about-description p { margin: 0 0 12px; }
.about-description ul { margin: 0 0 12px 18px; padding-left: 18px; }
.about-description li { margin: 6px 0; }

.vacancy-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.vacancy-title-overlay-text {
  font-size: 22px;
  font-family: "Eastman-Bold", sans-serif;
  color: white;
  margin: 0;
  letter-spacing: -0.5px;
}

.vacancy-date-inline {
  font-size: 11px;
  font-family: "Eastman-Light", sans-serif;
  color: white;
  background: rgba(255, 255, 255, 0.18);
  padding: 5px 14px;
  border-radius: 100px;
  white-space: nowrap;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  letter-spacing: 0.3px;
}

[data-theme="light"] .vacancy-date-inline {
  color: #ffffff;
  background: rgba(0, 0, 0, 0.05);
}

.email-hint {
  display: inline-block;
  font-size: 12px;
  font-family: "Eastman-Regular", sans-serif;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(33, 115, 229, 0.1);
  border: 1px solid rgba(33, 115, 229, 0.4);
  border-radius: 12px;
  padding: 6px 12px;
  margin-left: 30px;
  margin-top: 10px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  white-space: nowrap;
  max-width: fit-content;
  opacity: 0;
  animation: slideIn 0.5s ease forwards;
}

[data-theme="light"] .email-hint {
  color: #2173E5;
  background: rgba(33, 115, 229, 0.05);
  border: 1px solid rgba(33, 115, 229, 0.3);
}

@keyframes slideIn {
  0% {
    opacity: 0;
    transform: translateY(6px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.admin-delete-block {
  display: flex;
  justify-content: flex-start;
  margin: 40px 0 60px;
  margin-left: calc(50% - 380px);
}

.delete-button {
  background: linear-gradient(90deg, #ffa8a8, #db56b8);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 28px;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.delete-button:hover {
  transform: scale(1.05);
  opacity: 0.9;
}

@media (max-width: 430px) {

/* ========================================
   MAIN LAYOUT
======================================== */

.vacancy-page-layout {
  width: 100%;
  overflow-x: hidden;
}

.vacancy-scrollable-content {
  padding-top: 10px;
  padding-bottom: 20px;
}

.vacancy-container {
  width: 100%;
  padding: 0 10px;
  box-sizing: border-box;
}

/* ========================================
   LEFT BLOCK
======================================== */

.vacancy-left-block {
  width: 100%;
  margin-left: 0;
  border-radius: 14px;
  overflow: hidden;
  gap: 18px;
}

.vacancy-section {
  width: 100%;
}

/* ========================================
   IMAGE
======================================== */

.vacancy-image-wrapper {
  height: 220px;
  border-radius: 14px 14px 0 0;
}

.vacancy-image {
  border-radius: 0;
}

/* ========================================
   TITLE
======================================== */

.vacancy-title-overlay {
  padding: 16px;
  height: auto;
}

.vacancy-title-overlay-text {
  font-size: 20px;
  line-height: 1.15;
}

.vacancy-company {
  font-size: 14px;
}

.vacancy-city-badge {
  font-size: 11px;
  padding: 4px 10px;
}

.vacancy-date-inline {
  font-size: 10px;
  padding: 4px 10px;
}

/* ========================================
   LIKE + APPLY
======================================== */

.vacancy-title-wrapper {
  width: 100%;
  margin-left: 0;
  margin-top: 16px;
  padding: 0 16px;
  box-sizing: border-box;
}

.like-icon {
  width: 22px;
  height: 22px;
}

.email-hint {
  margin-left: 16px;
  margin-top: -6px;
  font-size: 11px;
  max-width: calc(100% - 32px);
  white-space: normal;
}

/* ========================================
   DESCRIPTION
======================================== */

.about-vacancy {
  width: calc(100% - 32px);
  margin: 0 16px;
  margin-top: 10px;
}

.about-title {
  font-size: 14px;
}

.about-description {
  font-size: 14px;
  line-height: 1.6;
}

/* ========================================
   TAGS
======================================== */

.vacancy-hashtags {
  margin-left: 16px;
  margin-right: 16px;
  margin-top: 20px;
  gap: 8px;
}

.hashtag {
  font-size: 12px;
  padding: 6px 12px;
}

/* ========================================
   COMPANY PANEL
======================================== */

.company-panel {
  position: static;
  width: 100%;
  margin-top: 20px;
}

.company-meta-card {
  width: 100%;
  margin-top: 16px;
}

/* ========================================
   OFFICE
======================================== */

.office-block {
  width: 100%;
  margin-left: 0;
  margin-top: 20px;
  border-radius: 14px;
  gap: 18px;
}

.office-title {
  margin-left: 16px;
  margin-top: 20px;
  margin-bottom: -10px;
  font-size: 14px;
}

.office-address {
  margin-left: 16px;
  margin-right: 16px;
  font-size: 13px;
}

/* ========================================
   MAP
======================================== */

.map-wrapper {
  width: 100%;
  height: 240px;
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
}

/* ========================================
   ADMIN BUTTON
======================================== */

.admin-delete-block {
  margin-left: 0;
  justify-content: center;
  margin-top: 24px;
}

.delete-button {
  width: calc(100% - 20px);
  height: 48px;
  border-radius: 14px;
}

/* ========================================
   SKELETON
======================================== */

.skeleton-container {
  padding-left: 0;
  padding: 10px;
}

.skeleton-image {
  width: 100%;
  height: 220px;
  border-radius: 14px;
}

.skeleton-title {
  width: 70%;
}

.skeleton-line {
  width: 100%;
}

.skeleton-line.short {
  width: 80%;
}

/* ========================================
   TOAST
======================================== */

.toast {
  width: calc(100% - 40px);
  text-align: center;
  font-size: 13px;
}

.vacancy-title-wrapper {
display: none;
}

}

.apply-button {
  position: relative;

  min-width: 170px;
  height: 44px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  padding: 2px 24px;
  margin-left: 30px;
  margin-top: -30px;
  color: #ffffff;
  background:
    linear-gradient(
      135deg,
      #3195f8 0%,
      #2173e5 55%,
      #315fd8 100%
    );

  border: none;
  border-radius: 30px;

  cursor: pointer;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;
  white-space: nowrap;

  box-shadow:
    0 7px 17px rgba(33, 115, 229, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);

  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    opacity 0.25s ease,
    background 0.25s ease;
}

.apply-button:hover:not(:disabled) {
  opacity: 1;
  transform: translateY(-2px);

  box-shadow:
    0 10px 23px rgba(33, 115, 229, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
}

.apply-button:active:not(:disabled) {
  transform: translateY(0) scale(0.98);
}

.apply-button:disabled {
  cursor: default;
}

.apply-button.loading {
  cursor: wait;

  background:
    linear-gradient(
      110deg,
      #287edc 0%,
      #3c91ec 45%,
      #4569df 100%
    );

  box-shadow:
    0 7px 18px rgba(33, 115, 229, 0.2);
}

.apply-button.applied {
  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #31b674,
      #269860
    );

  box-shadow:
    0 7px 18px rgba(38, 152, 96, 0.2);
}

.apply-default-content,
.apply-loading-content {
  position: relative;
  z-index: 2;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;
}

.apply-spinner {
  width: 17px;
  height: 17px;

  flex-shrink: 0;

  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;

  animation:
    apply-spinner-rotation 0.7s linear infinite;
}

.apply-success-icon {
  width: 17px;
  height: 17px;

  animation:
    apply-success-appearance 0.4s
    cubic-bezier(0.34, 1.56, 0.64, 1);
}

.apply-button-shine {
  position: absolute;
  top: -60%;
  left: -45%;
  z-index: 1;

  width: 30%;
  height: 220%;

  pointer-events: none;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.26),
      transparent
    );

  transform: rotate(20deg);

  animation:
    apply-shine 1.2s ease-in-out infinite;
}

/* Существующий email-вариант */

.apply-button-email {
  color: #2173e5;
  background: transparent;

  border: 2px solid #2173e5;

  box-shadow: none;
}

.apply-button-email:hover:not(:disabled) {
  background: rgba(33, 115, 229, 0.08);

  box-shadow:
    0 7px 18px rgba(33, 115, 229, 0.1);
}

/* Смена содержимого кнопки */

.apply-content-enter-active,
.apply-content-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.apply-content-enter-from {
  opacity: 0;
  transform: translateY(5px);
}

.apply-content-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Анимации */

@keyframes apply-spinner-rotation {
  to {
    transform: rotate(360deg);
  }
}

@keyframes apply-shine {
  0% {
    left: -45%;
  }

  60%,
  100% {
    left: 125%;
  }
}

@keyframes apply-success-appearance {
  from {
    opacity: 0;
    transform: scale(0.4) rotate(-25deg);
  }

  to {
    opacity: 1;
    transform: scale(1) rotate(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .apply-button,
  .apply-content-enter-active,
  .apply-content-leave-active {
    transition: none;
  }

  .apply-spinner,
  .apply-button-shine,
  .apply-success-icon {
    animation: none;
  }
}

.like-button {
  position: relative;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin: 0 0 0 -5px;
  padding: 0;
  margin-top: 10px;
  overflow: visible;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  background: transparent;
  border: none;
  border-radius: 50%;
  outline: none;
  box-shadow: none;

  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.like-button:hover:not(:disabled) {
  transform: scale(1.1);
}

.like-button:active:not(:disabled) {
  transform: scale(0.88);
}

.like-button:disabled {
  cursor: wait;
}

/*
  Обводка появляется только при навигации
  с клавиатуры, мышкой она не показывается.
*/
.like-button:focus-visible {
  outline: 2px solid
    rgba(33, 115, 229, 0.3);

  outline-offset: 3px;
}

/* Иконка */

.like-icon {
  position: relative;
  z-index: 2;

  width: 22px;
  height: 20px;

  display: block;
  object-fit: contain;

  transition:
    opacity 0.18s ease,
    transform 0.22s ease;
}

.like-button.loading .like-icon {
  opacity: 0.18;
  transform: scale(0.75);
}

.like-button.success .like-icon {
  animation:
    like-success-pop 0.5s
    cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Loader */

.like-spinner {
  position: absolute;
  inset: 2px;
  z-index: 3;

  border: 2px solid
    rgba(33, 115, 229, 0.14);

  border-top-color: #2173e5;
  border-radius: 50%;

  animation:
    like-spinner-rotation 0.7s linear infinite;
}

/*
  Лёгкая волна после успешного запроса.
  Без фона и постоянных обводок.
*/

.like-ripple {
  position: absolute;
  inset: 3px;
  z-index: 1;

  pointer-events: none;

  border: 2px solid
    rgba(239, 65, 94, 0.36);

  border-radius: 50%;

  animation:
    like-ripple-animation 0.55s ease-out forwards;
}

/* Vue transition */

.like-loader-enter-active,
.like-loader-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}

.like-loader-enter-from,
.like-loader-leave-to {
  opacity: 0;
  transform: scale(0.6);
}

/* Animations */

@keyframes like-spinner-rotation {
  to {
    transform: rotate(360deg);
  }
}

@keyframes like-success-pop {
  0% {
    transform: scale(0.7);
  }

  45% {
    transform: scale(1.3) rotate(-7deg);
  }

  72% {
    transform: scale(0.94) rotate(2deg);
  }

  100% {
    transform: scale(1) rotate(0);
  }
}

@keyframes like-ripple-animation {
  0% {
    opacity: 0.75;
    transform: scale(0.55);
  }

  100% {
    opacity: 0;
    transform: scale(1.55);
  }
}

/* Dark theme */

:global(html[data-theme="dark"]) .like-spinner {
  border-color:
    rgba(101, 164, 247, 0.17);

  border-top-color: #65a4f7;
}

:global(html[data-theme="dark"]) .like-button:focus-visible {
  outline-color:
    rgba(101, 164, 247, 0.35);
}

/* Reduced motion */

@media (prefers-reduced-motion: reduce) {
  .like-button,
  .like-icon,
  .like-loader-enter-active,
  .like-loader-leave-active {
    transition: none;
  }

  .like-spinner,
  .like-button.success .like-icon,
  .like-ripple {
    animation: none;
  }
}

/* ============================================================
   HERO INFO
============================================================ */

.vacancy-hero-info {
  width: 100%;

  display: flex;
  flex-direction: column;

  gap: 11px;
}


/* ============================================================
   TITLE + DATE
============================================================ */

.vacancy-hero-top {
  position: relative;

  display: block;

  width: 100%;
}


.vacancy-title-overlay-text {
  max-width: 760px;

  margin: 0;

  color: #ffffff;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 28px;

  line-height: 1.1;

  letter-spacing: -0.7px;

  text-wrap: balance;

  text-shadow:
    0 3px 18px
    rgba(0, 0, 0, 0.28);
}


.vacancy-meta-row {
  display: flex;
  align-items: center;

  gap: 9px;

  flex-wrap: wrap;
}



.vacancy-date-inline {
  flex-shrink: 0;

  display: inline-flex;
  align-items: center;

  gap: 7px;

  min-height: 31px;

  padding:
    0
    12px;

  color:
    rgba(
      255,
      255,
      255,
      0.9
    );

  background:
    rgba(
      20,
      24,
      32,
      0.34
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.13
    );

  border-radius: 10px;

  backdrop-filter:
    blur(12px);

  -webkit-backdrop-filter:
    blur(12px);

  box-shadow:
    0 6px 18px
    rgba(
      0,
      0,
      0,
      0.12
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  white-space: nowrap;
}


.date-dot {
  width: 5px;
  height: 5px;

  flex-shrink: 0;

  background: #ffffff;

  border-radius: 50%;

  opacity: 0.72;
}


/* ============================================================
   COMPANY + CITY
============================================================ */

.vacancy-hero-meta {
  display: flex;
  align-items: center;

  gap: 10px;

  flex-wrap: wrap;
}


.vacancy-company-line {
  display: inline-flex;
  align-items: baseline;

  gap: 4px;

  min-width: 0;
}



.company-prefix {
  color:
    rgba(
      255,
      255,
      255,
      0.55
    );

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
}


.vacancy-company {
  max-width: 420px;

  overflow: hidden;

  color:
    rgba(
      255,
      255,
      255,
      0.94
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 13px;

  text-overflow: ellipsis;

  white-space: nowrap;
}


.vacancy-city-badge {
  display: inline-flex;
  align-items: center;

  gap: 5px;

  min-height: 27px;

  padding:
    0
    9px;

  color:
    rgba(
      255,
      255,
      255,
      0.92
    );

  background:
    rgba(
      255,
      255,
      255,
      0.11
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.13
    );

  border-radius: 9px;

  backdrop-filter:
    blur(10px);

  -webkit-backdrop-filter:
    blur(10px);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  white-space: nowrap;
}


.vacancy-city-badge svg {
  width: 12px;
  height: 12px;

  flex-shrink: 0;
}


/* ============================================================
   MOBILE
============================================================ */

@media (
  max-width: 430px
) {

  .vacancy-hero-info {
    gap: 8px;
  }


  .vacancy-hero-top {
    align-items: flex-start;

    flex-direction: column;

    gap: 8px;
  }


  .vacancy-title-overlay-text {
    max-width: 100%;

    font-size: 22px;

    line-height: 1.12;
  }


  .vacancy-date-inline {
    min-height: 27px;

    padding:
      0
      9px;

    font-size: 8px;
  }


  .vacancy-hero-meta {
    gap: 7px;
  }



  .vacancy-company {
  max-width: 300px;

  overflow: hidden;

  color:
    rgba(
      255,
      255,
      255,
      0.95
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  text-overflow: ellipsis;

  white-space: nowrap;
}


  .company-prefix {
    font-size: 10px;
  }


  .vacancy-city-badge {
    min-height: 25px;

    font-size: 8px;
  }

}
  /* ============================================================
   CITY
============================================================ */

.vacancy-city-badge {
  display: inline-flex;
  align-items: center;

  gap: 5px;

  min-height: 26px;

  padding:
    0
    9px;

  color:
    rgba(
      255,
      255,
      255,
      0.92
    );

  background:
    rgba(
      255,
      255,
      255,
      0.11
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.13
    );

  border-radius: 8px;

  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  white-space: nowrap;
}


.vacancy-city-badge svg {
  width: 11px;
  height: 11px;

  flex-shrink: 0;
}


/* ============================================================
   PUBLISHED DATE
============================================================ */

.vacancy-published-date {
  display: inline-flex;
  align-items: baseline;

  gap: 4px;

  color:
    rgba(
      255,
      255,
      255,
      0.88
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  white-space: nowrap;
}


.published-prefix {
  color:
    rgba(
      255,
      255,
      255,
      0.5
    );

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;
}

/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 430px) {

  .vacancy-title-overlay-text {
    max-width: 100%;

    font-size: 21px;

    line-height: 1.13;
  }


  .vacancy-meta-row {
    gap: 7px;
  }


  .vacancy-company {
    max-width: 180px;

    font-size: 10px;
  }


  .company-prefix,
  .vacancy-published-date {
    font-size: 9px;
  }


  .vacancy-city-badge {
    min-height: 24px;

    padding:
      0
      8px;

    font-size: 8px;
  }


}

.vacancy-published-date {
  display: inline-flex;
  align-items: center;

  gap: 5px;

  min-height: 26px;

  padding:
    0
    9px;

  color:
    rgba(
      255,
      255,
      255,
      0.92
    );

  background:
    rgba(
      255,
      255,
      255,
      0.11
    );

  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.13
    );

  border-radius: 8px;

  backdrop-filter:
    blur(10px);

  -webkit-backdrop-filter:
    blur(10px);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  white-space: nowrap;
}

html[data-theme="dark"] .company-meta-card {
  background:
    linear-gradient(
      145deg,
      #22242a 0%,
      #1d1f24 100%
    );

  border:
    1px solid
    rgba(255, 255, 255, 0.07);

  box-shadow:
    0
    16px
    40px
    rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"] .meta-section {
  background: transparent;
}

html[data-theme="dark"] .meta-divider {
  background:
    rgba(255, 255, 255, 0.055);
}

html[data-theme="dark"] .meta-title {
  color: #747981;
}

html[data-theme="dark"] .meta-value {
  color: #f0f1f3;
}

html[data-theme="dark"] .meta-icon-dollar {
  color: #91b6ee;
}

/* ============================================================
   UNIFIED DARK CARDS
   основной блок + офис + мета
============================================================ */

html[data-theme="dark"] .vacancy-left-block,
html[data-theme="dark"] .office-block,
html[data-theme="dark"] .company-meta-card {
  background:
    linear-gradient(
      145deg,
      #22242a 0%,
      #1d1f24 100%
    );

  border:
    1px solid
    rgba(255, 255, 255, 0.07);

  box-shadow:
    0
    16px
    40px
    rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"] .vacancy-section {
  background: transparent;

  border:
    1px solid
    rgba(255, 255, 255, 0.055);
}

.about-description {
  max-width: 650px;

  color: #666b72;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 13px;

  line-height: 1.48;

  letter-spacing: -0.02px;
}


.about-description :deep(.description-heading) {
  margin:
    0
    0
    5px;

  color: #4f545b;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  font-weight: 400;

  line-height: 1.3;
}


.about-description :deep(.description-block) {
  margin:
    0
    0
    16px;
}


.about-description :deep(ul) {
  display: flex;
  flex-direction: column;

  gap: 1px;

  margin: 0;
  padding: 0;

  list-style: none;
}


.about-description :deep(li) {
  position: relative;

  margin: 0;

  padding-left: 14px;

  color: #666b72;

  line-height: 1.42;
}

/* ============================================================
   ADMIN ACTIONS
============================================================ */

.admin-actions-block {
  width: 740px;

  display: flex;
  align-items: center;

  gap: 12px;

  margin:
    28px
    0
    60px;

  margin-left:
    calc(50% - 630px);
}


/* ============================================================
   BASE BUTTON
============================================================ */

.edit-vacancy-button,
.delete-button {
  min-height: 44px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding:
    0
    18px;

  border-radius: 12px;

  cursor: pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease,
    background-color 0.2s ease;
}


.edit-vacancy-button svg,
.delete-button svg {
  width: 16px;
  height: 16px;

  flex-shrink: 0;
}


/* ============================================================
   EDIT
============================================================ */

.edit-vacancy-button {
  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #4d88df,
      #6470df
    );

  border: none;

  box-shadow:
    0 7px 17px
    rgba(
      67,
      105,
      195,
      0.18
    );
}


.edit-vacancy-button:hover {
  transform:
    translateY(-2px);

  box-shadow:
    0 11px 23px
    rgba(
      67,
      105,
      195,
      0.25
    );
}


/* ============================================================
   DELETE
============================================================ */

.delete-button {
  color: #cf5962;

  background:
    rgba(
      211,
      76,
      88,
      0.055
    );

  border:
    1px solid
    rgba(
      211,
      76,
      88,
      0.13
    );
}


.delete-button:hover {
  transform:
    translateY(-2px);

  color: #ffffff;

  background:
    #d45b65;

  border-color:
    #d45b65;

  box-shadow:
    0 9px 20px
    rgba(
      204,
      79,
      91,
      0.18
    );
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.delete-button {
  color: #e9848c;

  background:
    rgba(
      219,
      84,
      96,
      0.07
    );

  border-color:
    rgba(
      226,
      100,
      110,
      0.13
    );
}


html[data-theme="dark"]
.delete-button:hover {
  color: #ffffff;

  background:
    #cb5661;

  border-color:
    #cb5661;
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 430px) {

  .admin-actions-block {
    width: 100%;

    flex-direction: column;

    gap: 10px;

    margin:
      24px
      0
      38px;

    padding:
      0
      10px;
  }


  .edit-vacancy-button,
  .delete-button {
    width: 100%;
    height: 48px;

    border-radius: 14px;

    font-size: 11px;
  }

}

</style>